import {
  dismissToast,
  notifyError,
  notifyWarning,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  useCompany,
  useInvoiceReportDownload,
} from '@omnipresentgroup/domains';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import {
  FileFormat,
  type InvoiceReportNetSuiteType,
} from '@omnipresentgroup/types';
import { useCallback, useRef } from 'react';
import type { WretchResponse } from 'wretch/types';

import { WrappedMixpanel } from '../../../mixpanelWrapper';

const ERROR_TITLE = 'Error';
const ERROR_MESSAGE = 'Something went wrong while downloading the invoice';

export const useInvoiceDownload = (companyId: string) => {
  const downloadInvoiceReport = useInvoiceReportDownload();

  const downloadLink = useRef<HTMLAnchorElement>(null);

  const { data: company } = useCompany({
    id: companyId ?? '',
    enabled: !!companyId,
  });

  const downloadInvoice = useCallback(
    async (
      id: string,
      status: string,
      reference: string,
      type: InvoiceReportNetSuiteType,
      format: FileFormat,
    ) => {
      WrappedMixpanel.track(`Download Invoice Report ${format.toUpperCase()}`, {
        id,
        status,
        reference,
        type: toSentenceCase(type),
      });

      const toastId = notifyWarning(
        {
          title: `${format} Download for ${id} will start soon`,
          description: (
            <Typography as="span" size="14">
              This can take a moment&nbsp; do not leave this page
            </Typography>
          ),
        },
        {
          autoClose: false,
          progress: 100,
          hideProgressBar: false,
        },
      );

      downloadInvoiceReport(companyId, id, format, type)
        .then(async (response) => {
          const responseContent = await (response as WretchResponse).text();

          if (!downloadLink || !downloadLink.current) {
            notifyError({
              title: ERROR_TITLE,
              description: ERROR_MESSAGE,
            });

            return;
          }

          if (format === FileFormat.CSV) {
            const csvData: string = encodeURIComponent(responseContent);
            downloadLink.current.setAttribute(
              'href',
              `data:text/csv;charset=utf-8,${csvData}`,
            );
          }

          if (format === FileFormat.PDF) {
            const fileBase64 = responseContent;
            downloadLink.current.setAttribute(
              'href',
              `data:application/pdf;base64,${fileBase64}`,
            );
          }

          downloadLink.current.setAttribute(
            'download',
            `${company?.name.split(' ').join('-')}_${id}.${format}`,
          );

          downloadLink.current.click();

          dismissToast(toastId);
        })
        .catch(() => {
          notifyError({
            title: ERROR_TITLE,
            description: ERROR_MESSAGE,
          });

          dismissToast(toastId);
        });
    },
    [company?.name, companyId, downloadInvoiceReport],
  );

  return { downloadInvoice, downloadLink };
};
